import * as React from 'react';
import { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import * as actions from 'redux/actions/notificationActions';
import * as actions_comment from 'redux/actions/commentActions';
import { Translate, I18n } from 'react-redux-i18n';
import { useHistory } from 'react-router-dom';
import { Bell, Email } from 'theme/icon';

function renderNotificationMessage(message, kind) {
  const text = String(message || '').trim();
  if (!text) {
    return '';
  }
  if (kind === 'SYSTEM' || kind === 'ALERT' || /\s/.test(text)) {
    return text;
  }
  const translated = I18n.t(text);
  return translated !== text ? translated : text;
}

function Notification(props) {
  const history = useHistory();
  const [isOpen, setIsOpen] = React.useState(false);
  const [markingAll, setMarkingAll] = React.useState(false);
  const rootRef = useRef(null);

  const toggleOpen = () => {
    setIsOpen((open) => {
      const next = !open;
      if (next) {
        props.loadList({ id: props.user_id }, props.token);
      }
      return next;
    });
  };

  const close = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        close();
      }
    };

    const onEscape = (event) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  const onClickItem = (item) => {
    close();
    if (item.kind === 'ALERT') {
      history.push('/alerts');
      return;
    }
    if (item.kind === 'SYSTEM') {
      return;
    }
    props.loadItem({ comment_id: item.aux_id, id: props.user_id }, props.token).then(() => {
      history.push(`/comment?id=${item.aux_id}`);
    });
  };

  const unreadCount = props.count !== null && props.count !== undefined
    ? props.count
    : (props.sessionCount ?? 0);
  const countLabel = unreadCount > 0 ? String(unreadCount) : '';

  const onMarkAllRead = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (markingAll || unreadCount <= 0) {
      return;
    }
    setMarkingAll(true);
    await props.markAllRead({ id: props.user_id }, props.token);
    setMarkingAll(false);
  };

  return (
    <div className="notification-root" ref={rootRef}>
      <button
        type="button"
        className="icon-button notification-trigger"
        aria-label={I18n.t('notifications')}
        aria-expanded={isOpen}
        onClick={toggleOpen}
      >
        {countLabel ? (
          <span className="notification-badge">{countLabel}</span>
        ) : null}
        <Bell size={22} />
      </button>

      {isOpen && (
        <>
          <div className="notification-backdrop" aria-hidden="true" />
          <div className="notification-panel" role="dialog" aria-label={I18n.t('notifications')}>
            <div className="notification-panel-header">
              <div className="notification-panel-header-text">
                <h3 className="notification-panel-title">
                  <Translate value="notifications" />
                </h3>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="notification-mark-all"
                    onClick={onMarkAllRead}
                    disabled={markingAll}
                  >
                    <Translate value="notification_mark_all_read" />
                  </button>
                )}
              </div>
              <button
                type="button"
                className="notification-panel-close"
                aria-label="Close"
                onClick={close}
              >
                ×
              </button>
            </div>
            <div className="notification-panel-body">
              {props.loading ? (
                <p className="notification-empty">
                  <Translate value="report_loading" />
                </p>
              ) : props.error ? (
                <p className="notification-empty">
                  <Translate value="try_later" />
                </p>
              ) : props.list.length === 0 ? (
                <p className="notification-empty">
                  <Translate value="notification_empty" />
                </p>
              ) : (
                <ul className="notification-list">
                  {props.list.map((item) => (
                    <li key={item.id} className="notification-item">
                      <button
                        type="button"
                        className="notification-item-btn"
                        onClick={() => onClickItem(item)}
                      >
                        <span className="notification-item-icon" aria-hidden="true">
                          <Email />
                        </span>
                        <span className="notification-item-body">
                          <span className="notification-item-message">
                            {renderNotificationMessage(item.message, item.kind)}
                          </span>
                          <span className="notification-item-detail">{item.detail}</span>
                          <span className="notification-item-time">{item.created_at}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.token,
    count: reducer.notification.count,
    sessionCount: reducer.session.auth.notification,
    list: reducer.notification.list,
    error: reducer.notification.error,
    loading: reducer.notification.loading,
  };
}

const mapDispatchToProps = (dispatch) => ({
  loadList: (params, token) =>
    dispatch(actions.loadList({ params, token })),
  markAllRead: (params, token) =>
    dispatch(actions.markAllRead({ params, token })),
  loadItem: (params, token) =>
    dispatch(actions_comment.loadItem({ params, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Notification);
